import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Globe, Users, Eye, ShoppingCart, MousePointer, Radio, Maximize2 } from 'lucide-react';
import { liveVisitorsApi, type LiveVisitor, type LiveEvent } from '../../api/endpoints/liveVisitors';
import { StatCard } from '../../components/ui/StatCard';
import { Badge } from '../../components/ui/Badge';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { formatRelativeTime, formatCountryName } from '../../lib/formatters';
import { GlobeVisualization, type CountryPoint } from '../../components/analytics/GlobeVisualization';

const EVENT_COLORS: Record<string, string> = {
  page_view: 'text-success',
  product_view: 'text-info',
  add_to_cart: 'text-warning',
  checkout_start: 'text-accent',
  checkout_complete: 'text-success',
  search: 'text-text-secondary',
};

const EVENT_ICONS: Record<string, typeof Eye> = {
  page_view: Eye,
  product_view: MousePointer,
  add_to_cart: ShoppingCart,
};

export function LiveVisitorsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [activeCount, setActiveCount] = useState(0);
  const [visitors, setVisitors] = useState<LiveVisitor[]>([]);
  const [events, setEvents] = useState<(LiveEvent & { id: number })[]>([]);
  const [connected, setConnected] = useState(false);
  const [merchantGeo, setMerchantGeo] = useState<{ lat: number; lng: number } | null>(null);
  const eventIdRef = useRef(0);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // Fetch initial data
  const { data: recentData, isLoading } = useQuery({
    queryKey: ['live-recent', storeId],
    queryFn: () => liveVisitorsApi.getRecent(storeId!),
    enabled: !!storeId,
    refetchInterval: connected ? false : 10000,
  });

  // SSE connection
  useEffect(() => {
    if (!storeId) return;
    const url = liveVisitorsApi.getStreamUrl(storeId);
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = () => setConnected(true);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'active_count') {
          setActiveCount(data.count);
          setVisitors(data.visitors ?? []);
          if (data.merchantGeo?.lat && data.merchantGeo?.lng) setMerchantGeo({ lat: data.merchantGeo.lat, lng: data.merchantGeo.lng });
        } else if (data.type === 'event') {
          const id = ++eventIdRef.current;
          const prev = eventsRef.current;
          setEvents([{ ...data, id }, ...prev].slice(0, 50));
        }
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => setConnected(false);

    return () => { es.close(); setConnected(false); };
  }, [storeId]);

  // Use recent data as fallback
  useEffect(() => {
    if (recentData && !connected) {
      setActiveCount(recentData.activeCount ?? 0);
      setVisitors(recentData.visitors ?? []);
    }
  }, [recentData, connected]);

  // Country aggregation
  const countryStats = useCallback(() => {
    const map = new Map<string, { count: number; lat: number; lon: number }>();
    for (const v of visitors) {
      if (!v.geo?.country) continue;
      const existing = map.get(v.geo.country);
      if (existing) {
        existing.count++;
      } else {
        map.set(v.geo.country, { count: 1, lat: v.geo.lat, lon: (v.geo as any).lng ?? v.geo.lon });
      }
    }
    return Array.from(map.entries())
      .map(([country, data]) => ({ country, ...data }))
      .sort((a, b) => b.count - a.count);
  }, [visitors]);

  const countries = countryStats();
  const totalPages = visitors.reduce((s, v) => s + (v.pageCount || 0), 0);

  // Map country stats into the format the globe component expects
  const globeCountries = useMemo<CountryPoint[]>(
    () =>
      countries.map((c) => ({
        name: c.country,
        count: c.count,
        lat: c.lat,
        lng: c.lon,
      })),
    [countries],
  );

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/${storeId}/analytics`)}
          className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold flex-1">{t('liveVisitors.title', 'Live Visitors')}</h1>
        <Badge variant={connected ? 'success' : 'warning'}>
          <Radio className="h-3 w-3" />
          {connected ? t('liveVisitors.connected', 'Live') : t('liveVisitors.disconnected', 'Reconnecting...')}
        </Badge>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <StatCard
          title={t('liveVisitors.activeNow', 'Active Now')}
          value={activeCount}
          icon={<Users className="h-5 w-5" />}
        />
        <StatCard
          title={t('liveVisitors.countries', 'Countries')}
          value={countries.length}
          icon={<Globe className="h-5 w-5" />}
        />
        <StatCard
          title={t('liveVisitors.pageViews', 'Pages Viewed')}
          value={totalPages}
          icon={<Eye className="h-5 w-5" />}
        />
      </div>

      {/* 3D Globe Visualization */}
      <div className="relative mb-6">
        <GlobeVisualization countries={globeCountries} activeVisitors={activeCount} originGeo={merchantGeo} />
        <button
          onClick={() => navigate(`/${storeId}/analytics/globe`)}
          className="absolute bottom-4 start-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 rounded-full border border-white/20 bg-black/50 backdrop-blur-sm px-4 py-2 text-xs font-medium text-white hover:bg-black/70 transition-colors"
        >
          <Maximize2 className="h-3.5 w-3.5" />
          {t('liveVisitors.immersiveGlobe', 'Open Interactive Globe')}
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Active visitors */}
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Users className="h-4 w-4 text-accent" />
            {t('liveVisitors.activeVisitors', 'Active Visitors')} ({activeCount})
          </h2>
          {visitors.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8">{t('liveVisitors.noVisitors', 'No active visitors right now')}</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {visitors.map((v) => (
                <div key={v.sessionId} className="flex items-center justify-between rounded-lg bg-bg-tertiary p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium truncate">{v.page || '/'}</p>
                      {v.customerId && (
                        <Badge variant="info" className="text-[10px] shrink-0">
                          {t('analytics.customer', 'Customer')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-text-muted">
                      {v.geo?.country ? formatCountryName(v.geo.country, i18n.language) : t('liveVisitors.unknown', 'Unknown')}
                      {v.geo?.city ? `, ${v.geo.city}` : ''}
                      {' · '}{v.pageCount} {t('liveVisitors.pages', 'pages')}
                      {(v.duration ?? 0) > 0 && ` · ${Math.round((v.duration ?? 0) / 60)}m`}
                    </p>
                  </div>
                  <div className="h-2 w-2 rounded-full bg-success animate-pulse shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Live event feed */}
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Radio className="h-4 w-4 text-accent" />
            {t('liveVisitors.eventFeed', 'Live Feed')}
          </h2>
          {events.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8">{t('liveVisitors.waitingEvents', 'Waiting for events...')}</p>
          ) : (
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {events.map((ev) => {
                const Icon = EVENT_ICONS[ev.event] ?? Eye;
                const color = EVENT_COLORS[ev.event] ?? 'text-text-muted';
                return (
                  <div key={ev.id} className="flex items-center gap-2.5 rounded-lg px-3 py-2 hover:bg-bg-tertiary transition-colors">
                    <Icon className={`h-3.5 w-3.5 shrink-0 ${color}`} />
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-medium">{({
                        page_view: t('liveVisitors.pageView', 'Page View'),
                        product_view: t('liveVisitors.productView', 'Product View'),
                        add_to_cart: t('liveVisitors.addToCart', 'Add to Cart'),
                        checkout_start: t('liveVisitors.checkoutStart', 'Checkout Start'),
                        checkout_complete: t('liveVisitors.checkoutComplete', 'Checkout Complete'),
                        search: t('liveVisitors.search', 'Search'),
                      } as Record<string, string>)[ev.event] ?? ev.event.replace(/_/g, ' ')}</span>
                      {ev.page && <span className="text-xs text-text-muted ms-1.5 truncate">- {ev.title || ev.page}</span>}
                    </div>
                    {ev.customerId && (
                      <Badge variant="info" className="text-[10px] shrink-0">
                        {t('analytics.customer', 'Customer')}
                      </Badge>
                    )}
                    <span className="text-[10px] text-text-muted shrink-0">
                      {ev.geo?.country ? formatCountryName(ev.geo.country, i18n.language) : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Countries breakdown */}
        {countries.length > 0 && (
          <div className="rounded-xl border border-border-default bg-bg-secondary p-5 lg:col-span-2">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Globe className="h-4 w-4 text-accent" />
              {t('liveVisitors.byCountry', 'Visitors by Country')}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {countries.map((c) => {
                const pct = activeCount > 0 ? Math.round((c.count / activeCount) * 100) : 0;
                return (
                  <div key={c.country} className="flex items-center justify-between rounded-lg bg-bg-tertiary p-3">
                    <span className="text-sm font-medium">{formatCountryName(c.country, i18n.language)}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 rounded-full bg-bg-hover overflow-hidden">
                        <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-text-muted w-6 text-end">{c.count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
